/**
 * Intent Setter Component
 * 
 * Allows leaders to set strategic intent and priorities for control domains.
 * Part of the Control Bridge control surface.
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md - "Control Bridge" definition
 */

import React, { useState } from 'react';
import { Select, Typography, Tooltip, Tag } from 'antd';
import {
  AimOutlined,
  ThunderboltOutlined,
  RiseOutlined,
  PauseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import styles from './IntentSetter.module.css';

const { Text } = Typography;

export type IntentType = 'reduce-entropy' | 'increase-value' | 'maintain' | 'accelerate';

export interface DomainIntent {
  domainId: string;
  domainName: string;
  intent: IntentType;
  priority: number; // 1-10
}

interface IntentSetterProps {
  /** Domain identifier */
  domainId: string;
  /** Domain display name */
  domainName: string;
  /** Current intent (if any) */
  currentIntent?: IntentType;
  /** Current priority (if any) */
  currentPriority?: number;
  /** Callback when intent changes */
  onIntentChange?: (intent: IntentType) => void;
  /** Callback when priority changes */
  onPriorityChange?: (priority: number) => void;
  /** Whether in compact mode */
  compact?: boolean;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
}

const INTENT_OPTIONS: { value: IntentType; label: string; icon: React.ReactNode; description: string }[] = [
  {
    value: 'reduce-entropy',
    label: 'Reduce Entropy',
    icon: <ThunderboltOutlined />,
    description: 'Focus on improving coordination and reducing chaos',
  },
  {
    value: 'increase-value',
    label: 'Increase Value',
    icon: <RiseOutlined />,
    description: 'Prioritize value generation over coordination',
  },
  {
    value: 'maintain',
    label: 'Maintain',
    icon: <PauseOutlined />,
    description: 'Hold current position, balance entropy and value',
  },
  {
    value: 'accelerate',
    label: 'Accelerate',
    icon: <AimOutlined />,
    description: 'Push for rapid progress, accept higher entropy',
  },
];

const PRIORITY_LEVELS = [
  { value: 1, label: 'Critical', color: '#dc2626' },
  { value: 2, label: 'High', color: '#ef4444' },
  { value: 3, label: 'Elevated', color: '#f97316' },
  { value: 4, label: 'Above Normal', color: '#f59e0b' },
  { value: 5, label: 'Normal', color: '#eab308' },
  { value: 6, label: 'Below Normal', color: '#84cc16' },
  { value: 7, label: 'Low', color: '#22c55e' },
  { value: 8, label: 'Minimal', color: '#10b981' },
  { value: 9, label: 'Background', color: '#14b8a6' },
  { value: 10, label: 'Idle', color: '#06b6d4' },
];

const getPriorityInfo = (priority: number) => {
  return PRIORITY_LEVELS.find(p => p.value === priority) || PRIORITY_LEVELS[4]; // Default to Normal
};

export const IntentSetter: React.FC<IntentSetterProps> = ({
  domainId,
  domainName,
  currentIntent = 'maintain',
  currentPriority = 5,
  onIntentChange,
  onPriorityChange,
  compact = false,
  disabled = false,
  className,
}) => {
  const [intent, setIntent] = useState<IntentType>(currentIntent);
  const [priority, setPriority] = useState<number>(currentPriority);

  const handleIntentChange = (value: IntentType) => {
    setIntent(value);
    onIntentChange?.(value);
  };

  const handlePriorityChange = (value: number) => {
    setPriority(value);
    onPriorityChange?.(value);
  };

  const selectedIntent = INTENT_OPTIONS.find(opt => opt.value === intent);
  const priorityInfo = getPriorityInfo(priority);

  const containerClasses = [
    styles.container,
    compact && styles.compact,
    disabled && styles.disabled,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <div className={styles.header}>
        <Text className={styles.domainLabel}>{domainName}</Text>
        <Tooltip title="Set the strategic intent and priority for this control domain">
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </div>

      <div className={styles.controls}>
        {/* Intent Selector */}
        <div className={styles.intentSection}>
          {!compact && <Text type="secondary" className={styles.sectionLabel}>Intent</Text>}
          <Select
            value={intent}
            onChange={handleIntentChange}
            disabled={disabled}
            className={styles.intentSelect}
            popupMatchSelectWidth={false}
            classNames={{ popup: { root: styles.intentDropdown } }}
            suffixIcon={selectedIntent?.icon}
            options={INTENT_OPTIONS.map(opt => ({
              value: opt.value,
              label: (
                <div className={styles.optionContent}>
                  <span className={styles.optionIcon}>{opt.icon}</span>
                  <span className={styles.optionLabel}>{opt.label}</span>
                  {!compact && (
                    <Text type="secondary" className={styles.optionDesc}>
                      {opt.description}
                    </Text>
                  )}
                </div>
              ),
            }))}
          />
        </div>

        {/* Priority Selector */}
        <div className={styles.prioritySection}>
          {!compact && <Text type="secondary" className={styles.sectionLabel}>Priority</Text>}
          <Select
            value={priority}
            onChange={handlePriorityChange}
            disabled={disabled}
            className={styles.prioritySelect}
            options={PRIORITY_LEVELS.map(p => ({
              value: p.value,
              label: (
                <div className={styles.priorityOption}>
                  <Tag color={p.color} className={styles.priorityTag}>
                    {p.value}
                  </Tag>
                  <span>{p.label}</span>
                </div>
              ),
            }))}
          />
        </div>
      </div>

      {/* Current Status Display */}
      {!compact && (
        <div className={styles.statusDisplay}>
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>Active Intent:</span>
            <Tag icon={selectedIntent?.icon} className={styles.statusTag}>
              {selectedIntent?.label}
            </Tag>
          </div>
          <div className={styles.statusItem}>
            <span className={styles.statusLabel}>Priority:</span>
            <Tag color={priorityInfo.color} className={styles.statusTag}>
              {priorityInfo.label} ({priority})
            </Tag>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntentSetter;
